'use client';

import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useRef } from 'react';
import * as CookieHelper from '@/utils/cookie-helper';
import { PAGE_COUNT_COOKIE_NAME, QUALIFIED_USER_SAVED_COOKIE_NAME } from '@/utils/constants';
import { urlParameterCodes } from '@/constants/urlParams';
import { TrackingConversionType } from '@/utils/tracking-conversion-types';
import saveTrackingCookies from '@/integrations/tracking-service/cookies/track';
import { DataLayerHelper } from '@/utils/data-layer-helper';

const QUALIFIED_USER_PAGE_THRESHOLD = 2;
const QUALIFIED_USER_TIME_THRESHOLD_MS = 30000;

// The language/country selector adds this one-shot param to its forced-reload URL. When present the
// load is a locale switch (not a distinct content page): strip it — so it neither lingers in the URL
// nor counts on a later refresh — and report true.
const consumeLocaleSwitchMarker = (): boolean => {
  const searchParams = new URLSearchParams(globalThis.location.search);

  if (!searchParams.has(urlParameterCodes.localeSwitch)) {
    return false;
  }

  searchParams.delete(urlParameterCodes.localeSwitch);
  const query = searchParams.toString();
  const search = query ? `?${query}` : '';
  globalThis.history.replaceState(
    null,
    '',
    `${globalThis.location.pathname}${search}${globalThis.location.hash}`,
  );

  return true;
};

export function QualifiedUser() {
  const pathname = usePathname();
  const lastCountedPathnameRef = useRef<string | null>(null);
  const hasFiredRef = useRef(false);

  const triggerQualifiedUser = useCallback(async () => {
    // Fire the conversion at most once. hasFiredRef blocks synchronous re-entry (e.g. React
    // StrictMode's double-invoked effect) before the async dedupe cookie is written; the cookie
    // then covers future mounts and the server-side middleware path (src/middleware.ts).
    if (hasFiredRef.current || CookieHelper.getCookieValue(QUALIFIED_USER_SAVED_COOKIE_NAME)) {
      return;
    }

    hasFiredRef.current = true;
    await saveTrackingCookies(TrackingConversionType.QUALIFIED_USER);
    DataLayerHelper.pushQualifiedUser();
  }, []);

  useEffect(() => {
    if (CookieHelper.getCookieValue(QUALIFIED_USER_SAVED_COOKIE_NAME)) {
      return () => {};
    }

    let pageCount = Number.parseInt(CookieHelper.getCookieValue(PAGE_COUNT_COOKIE_NAME) ?? '', 10);

    if (Number.isNaN(pageCount)) {
      pageCount = 0;
    }

    // Count each distinct page at most once. Guarding on the pathname keeps a repeated effect run
    // for the same page (React StrictMode's double invocation) from inflating the count.
    if (lastCountedPathnameRef.current !== pathname) {
      lastCountedPathnameRef.current = pathname;

      // A locale switch forces a full reload to a different locale path; that is not a distinct
      // content pageview, so leave the count unchanged (but still strip the one-shot marker).
      if (!consumeLocaleSwitchMarker()) {
        pageCount += 1;
        document.cookie = `${PAGE_COUNT_COOKIE_NAME}=${pageCount}; path=/; SameSite=None; Secure`;
      }
    }

    if (pageCount >= QUALIFIED_USER_PAGE_THRESHOLD) {
      triggerQualifiedUser();
      return () => {};
    }

    const timer = setTimeout(() => {
      triggerQualifiedUser();
    }, QUALIFIED_USER_TIME_THRESHOLD_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [pathname, triggerQualifiedUser]);

  return null;
}
